import { useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { Formik, FormikProvider } from 'formik';

import { hasModule } from 'lib/helpers/utility';
import { useExportConfig } from 'lib/hooks/queries/export/useExportConfig';
import {
  ASYNC_EXPORT_DATASETS,
  ASYNC_EXPORT_ROW_THRESHOLD
} from 'lib/helpers/pdf/tableExport/lib/constants';

import { modulesState } from 'components/state';
import { showAlert } from 'components/shared/Alert/Alert';
import { currentPractice } from 'components/practice/practiceState';

import Footer from '../Footer/Footer';
import validationSchema from '../../lib/validationSchema';
import { getExportLabel } from '../../lib/getExportLabel';
import { showExportError } from '../../lib/showExportError';
import { fetchExportRows } from '../../lib/fetchExportRows';
import { getInitialValues } from '../../lib/getInitialValues';
import { useExportColumns } from '../../lib/useExportColumns';
import { queueTableExport } from '../../lib/queueTableExport';
import { exportSpreadsheet } from '../../lib/exportSpreadsheet';
import { resolveExportColumns } from '../../lib/resolveExportColumns';
import { exportPdfWithFallback } from '../../lib/exportPdfWithFallback';

import ContentHeader from './components/ContentHeader';
import ExportDataRadio from './components/ExportDataRadio';
import ExportOrientation from './components/ExportOrientation';
import {
  ActiveFilterList,
  ExportColumnsCheckbox,
  ExportInputTitle,
  ExportPageScope,
  ExportTypeCheckbox
} from './components';

const Content = ({ closePopover, useData = () => ({}), customContent }) => {
  const navigate = useNavigate();

  const practice = useRecoilValue(currentPractice);
  const practiceModules = useRecoilValue(modulesState);
  const hasClaims = hasModule(practiceModules, 'billing.claims');

  const {
    name,
    title,
    filters = {},
    colDefs,
    defaultColumns,
    getData,
    gridApi,
    statusPanelData,
    printExpandedRows,
    queryParams,
    patient,
    exportColumnsKeys,
    exportApiProps,
    onExportSubmit,
    page,
    limit,
    sortField,
    withTotals,
    data: tableData
  } = useData();

  const { columns, childColumns, hasExpandableRows } = useExportColumns({
    colDefs,
    defaultColumns,
    gridApi,
    exportColumnsKeys,
    printExpandedRows
  });

  const hasCustomSubmit = typeof onExportSubmit === 'function';
  const hasBackendExport = typeof getData === 'function';
  const { data: exportConfig } = useExportConfig();
  const { datasets = ASYNC_EXPORT_DATASETS, tableRowThreshold = ASYNC_EXPORT_ROW_THRESHOLD } =
    exportConfig?.config || {};

  const totalRows = Number(tableData?.count) || 0;
  const supportsQueuedExport = datasets.includes(name) && totalRows > tableRowThreshold;
  const hasBackendPagination = hasBackendExport && Number.isFinite(limit) && limit > 0;
  const isGridPaginated = (gridApi?.paginationGetTotalPages?.() ?? 0) > 1;
  const canSelectPageScope = hasBackendPagination || isGridPaginated;

  const initialValues = getInitialValues({ title, name, columns });

  const handleExport = async (values, otherProps) => {
    try {
      otherProps.setSubmitting(true);

      const { isDetailedMode, isExportAllColumns, finalColumns, finalParentColumns } =
        resolveExportColumns({ values, columns, childColumns, hasExpandableRows });

      const isAllPages = !canSelectPageScope || values.export_scope === 'all';

      if (supportsQueuedExport && isAllPages && !hasCustomSubmit) {
        const queued = await queueTableExport({
          navigate,
          values,
          finalColumns,
          dataset: name,
          datasetParams: { ...queryParams, filters, ...(sortField && { sort: sortField }) },
          statusPanelData,
          exportApiProps,
          totalRows
        });

        if (queued) closePopover?.();
        return;
      }

      const data = await fetchExportRows({
        navigate,
        finalColumns,
        isExportAllColumns,
        isCurrentPageOnly: !isAllPages,
        gridApi,
        getData,
        name,
        filters,
        queryParams,
        page,
        limit,
        sortField
      });

      if (!data.length) {
        showAlert({ title: 'No data to export', color: 'warning' });
        return;
      }

      if (hasCustomSubmit) {
        await onExportSubmit({ values, data, finalColumns, isExportAllColumns, filters });
        closePopover?.();
        return;
      }

      if (values.export_type.includes('csv') || values.export_type.includes('excel')) {
        exportSpreadsheet({
          values,
          data,
          finalColumns,
          finalParentColumns,
          isDetailedMode,
          hasExpandableRows,
          withTotals,
          statusPanelData
        });
      }

      if (values.export_type.includes('pdf')) {
        await exportPdfWithFallback({
          navigate,
          values,
          data,
          name,
          isDetailedMode,
          isExportAllColumns,
          finalColumns,
          finalParentColumns,
          statusPanelData,
          withTotals,
          headerConfigs: { practice, patient, hasClaims },
          exportApiProps
        });
      }
    } catch (error) {
      console.error('Error while exporting table:', error);
      showExportError(error);
    } finally {
      otherProps.setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleExport}
      validationSchema={validationSchema}>
      {(formValues) => (
        <FormikProvider
          value={{
            ...formValues,
            willQueueExport: supportsQueuedExport,
            parentColumns: columns,
            childColumns,
            columns:
              hasExpandableRows && formValues.values.export_data === 'detailed'
                ? childColumns
                : columns
          }}>
          <div className="flex flex-col ">
            <ContentHeader closePopover={closePopover} label={getExportLabel({ title, name })} />

            <div className="flex max-h-[400px] flex-col gap-4 overflow-y-auto p-4">
              <ExportInputTitle />
              {customContent}
              <ExportTypeCheckbox />
              <ExportOrientation />
              {hasExpandableRows && <ExportDataRadio />}
              {canSelectPageScope && <ExportPageScope />}
              <ExportColumnsCheckbox />
              <ActiveFilterList />
            </div>
          </div>
          <Footer closePopover={closePopover} />
        </FormikProvider>
      )}
    </Formik>
  );
};

export default Content;
